import {
  get as getProjection,
  getTransformFromProjections,
  getUserProjection,
  identityTransform,
  Projection,
  transform as transformCoordinate,
} from 'ol/proj';
import {toStringXY, degreesToStringHDMS} from 'ol/coordinate';
import BaseMousePosition from 'ol/control/MousePosition';
import Event from 'ol/events/Event';
import {dms2deg} from '@config/geographic';

export class ViewProjectionEvent extends Event {
  /**
   * @param {DrawEventType} type Type.
   * @param {Projection} projection The projection.
   */
  constructor(type, projection) {
    super(type);

    /**
     * The projection.
     * @type {Projection}
     * @api
     */
    this.projection = projection;
  }
}

class MousePosition extends BaseMousePosition {
    /**
    * @param {Object} [opt_options] Control options.
    */
    constructor(opt_options) {
        const options = opt_options || {};

        const className =
          options.className !== undefined
            ? options.className
            : 'ol-mouse-position';
        options.className = className;
        options.undefinedHTML = options.undefinedHTML || '&nbsp;';

        super(options);

        if (!this.getCoordinateFormat()) {
            this.setCoordinateFormat((coord) => this.coordinateFormat(coord));
        }

        /**
         * @private
         * @type {string}
         */
        this.separator_ = options.separator || ',';

        /**
         * @private
         * @type {number}
         */
        this.precision_ = options.precision || 4;

        /**
         * @private
         * @type {boolean}
         * If true, coordinates in WGS84 are shown in DMS format
         */
        this.displayDMS_ = options.displayDMS || false;

        /**
         * @private
         * @type {boolean}
         * If true, coordinates from the MousePosition control can be edited by users to re-center the view. False by default.
         */
        this.editCoordinates_ = options.editCoordinates || false;

        /**
         * @private
         * @type {function}
         * callback...
         */
        this.mapCenterCallback_ = options.mapCenterCallback || null;

        /**
         * @private
         * @type {boolean}
         */
        this.renderedVisible_ = false;

        /**
         * @private
         * @type {html}
         */
        this.form_div_ = null;

        /**
         * @private
         * @type {html}
         */
        this.innerElement_ = null;


         /**
         * @private
         * @type {html}
         */
        this.projectionSelect_ = null;

        this.createElements(options);

    }

    createElements(options) {
        const projections = options.projections || [];
        const container = this.element;

        const form = document.createElement('form');
        form.className = 'form-inline';

        const form_div = document.createElement('div');
        form_div.className = 'form-group form-group-sm flex-nowrap';
        this.form_div_ = form_div;

        if (options.editCoordinates) {
          let input = document.createElement("input");
          input.setAttribute('type', 'text');
          input.setAttribute('class', 'form-control control-input-label');
          input.addEventListener('keypress', (e) => {
            // if not 'enter key' just exit here
            if (e.key === 'Enter') {
              e.stopPropagation();
              e.preventDefault();

              let val = e.target.value;
              this.onMousePositionEditModeClick(e.target.value);
              // Cancel the default action
            }
          }, false);
          input.addEventListener('input', (e) => {
            let el = e.target;
            this.updateInputSize(el);
          }, false);
          this.innerElement_ = input;
          form_div.appendChild(input);

        }else{
          let label = document.createElement('div');
          label.className = 'control-label';
          form_div.appendChild(label);
          this.innerElement_ = label;
        }


        if(projections && projections.length>1){
            const select = document.createElement('select');
            this.projectionSelect_ = select;
            select.className = 'form-control';
            select.addEventListener('change', function(e) { this.onComboChange(e); }.bind(this), false);
            form_div.appendChild(select);

            for (var i = 0; i < projections.length; i++) {
                let option = document.createElement("option");
                option.value = projections[i].epsg;
                option.text = projections[i].name;
                select.appendChild(option);
            }
            let projection = this.getProjection();

            select.value = projection ? projection.getCode() : null;
        }

        form.appendChild(form_div);

        const CLASS_NAME_ACTIVE = 'active';
        const SELECTOR_DATA_TOGGLE = '[data-toggle="button"]';

        let button = document.createElement('button');
        button.setAttribute('type', 'button');
        button.setAttribute('autocomplete', 'off');
        button.setAttribute('class', this.renderedVisible_ ? CLASS_NAME_ACTIVE : '');
        button.title = options.tipLabel || '';

        let img = document.createElement('i');
        img.setAttribute('class', 'fas fa-mouse');
        button.append(img);
        button.setAttribute('data-toggle', 'button');
        button.setAttribute('aria-pressed', this.renderedVisible_);
        button.addEventListener('click', (e) => {
            let target = event.target;
            const button = target.closest(SELECTOR_DATA_TOGGLE);
            if (button) {
                const activeElement = button.classList.contains(CLASS_NAME_ACTIVE);
                    button.setAttribute('aria-pressed', !button.classList.contains(CLASS_NAME_ACTIVE));
                    if (activeElement) {
                        this.updateFormState(false);
                        button.classList.remove(CLASS_NAME_ACTIVE);
                        e.preventDefault();
                        e.stopPropagation();
                    }else{
                        this.updateFormState(true);
                        button.classList.add(CLASS_NAME_ACTIVE);
                    }
            }
        }, false);

        form.appendChild(button);
        container.appendChild(form);
        this.updateFormState(this.renderedVisible_);
    }

    onComboChange(evt) {
        let crs = evt.target.options[evt.target.selectedIndex].value;
        let projection = getProjection(crs);
        if(projection){
            this.setProjection(projection);
        }
    }

    onMousePositionEditModeClick(coords) {
      if (!this.editCoordinates_) {
        return;
      }

      let lonlat = this.getCoordinates(coords);
      if (!lonlat) {
        return;
      }

      const projection = this.getProjection();
      if (this.displayDMS_ && projection.getUnits() === "degrees") {
        this.locateDMSCoordinates(lonlat);
      } else {
        this.locateCoordinates(lonlat);
      }

      let mapCenterFunction = this.mapCenterCallback_;
      if (typeof mapCenterFunction === "function") {
        var view = this.getMap().getView();
        var center = view.getCenter();
        mapCenterFunction.call(this, center);
      }
    }

    /**
     * Get coordinate from inputs and select in decimal degrees
     *
     * @param {String} coordType - "Lon" or "Lat"
     * @returns {undefined}
     * @private
     */
    getCoordinates(coords) {
      coords = coords.split(this.separator_).map(element => element.trim());
      if (coords.length != 2) {
        return null;
      }

      if (coords[0] === null || coords[1] === null) {
        return null;
      }
      return coords;
    }

    locateCoordinates(lonlat) {
      let lon = parseFloat(lonlat[0]);
      if (isNaN(lon)) {
        return null;
      }
      let lat = parseFloat(lonlat[1]);
      if (isNaN(lat)) {
        return null;
      }

      this.applyCoordinates([lon, lat]);
    }

    locateDMSCoordinates(lonlat) {
      let lon = dms2deg(lonlat[0]);
      if (isNaN(lon)) {
        return null;
      }

      let lat = dms2deg(lonlat[1]);
      if (isNaN(lat)) {
        return null;
      }

      this.applyCoordinates([lon, lat]);
    }

    applyCoordinates(lonlat) {
      let xy = lonlat;
      const projection = this.getProjection();
      if(this.displayDMS_){
        if (projection.getAxisOrientation() === 'enu') {
          xy = lonlat;
        } else {
          xy = [lonlat[1], lonlat[0]];
        }
      }

      let coordinate = transformCoordinate(xy, projection, this.mapProjection_);
      if(!coordinate){
        return;
      }

      let bbox = this.mapProjection_.getExtent();
      if (bbox) { // check if coordinates are in the extent
            var extent = [bbox.left, bbox.bottom, bbox.right, bbox.top];
            if (coordinate[0] < extent[0] || coordinate[0] > extent[2]) {
                return;
            }
            if (coordinate[1] < extent[1] || coordinate[1] > extent[3]) {
                return;
            }
      }

      const map = this.getMap();
      let view = map.getView();
      view.setCenter(coordinate);
    }

    coordinateFormat(coord){
        const projection = this.getProjection() || this.mapProjection_;
        const units = projection ? projection.getUnits() : null;
        switch(units){
            case 'm':
              // let template = 'X: {x}, Y: {y}';
              // let template = this.editCoordinates_ ? `{x}${this.separator_} {y}` : `X: {x}${this.separator_} Y: {y}`;
              // return format(coord, template, this.precision_);
              return toStringXY(coord, this.precision_);
            case 'degrees':
              if(this.displayDMS_) {
                // out is now '47° 58′ 60.0″ N, 7° 50′ 60.0″ E'
                let fractionDigits = 0;
                return (
                  degreesToStringHDMS('NS', coord[1], fractionDigits) +
                  `${this.separator_} ` +
                  degreesToStringHDMS('EW', coord[0], fractionDigits)
                );
              }else{
                let fractionDigits = 8;
                return toStringXY(coord, fractionDigits);
              }
            default:
                return '';
        }
    }

    updateFormState(flag){
        this.renderedVisible_ = flag;
        if(this.form_div_){
            this.form_div_.style.display = flag ? '' : 'none';
        }
    }

    updateInputSize(el) {
      if(!el || el.innerHTML !== undefined) return;

      const string = el.value || el.getAttribute('placeholder') || '';
      // fakeEle.innerHTML = string.replace(/\s/g, '&' + 'nbsp;');

      el.style.width = ((string.length + 1) * 8) + "px";
    }

    /**
     * @param {Event} event Browser event.
     * @protected
     */
    handleMouseOut(event) {
        // this.updateHTML_(null);
    }

    /**
    * @param {MouseEvent} event Browser event.
    * @protected
    */
    handleMouseMove(event) {
        let target = event.target;
        if(target && target.nodeName == "CANVAS"){
            const map = this.getMap();
            this.updateHTML_(map.getEventPixel(event));
        }
    }

    /**
     * @param {?import("../pixel.js").Pixel} pixel Pixel.
     * @private
     */
    updateHTML_(pixel) {
        if(!this.renderedVisible_){
            return;
        }

        let val = this.placeholder_;
        if (pixel && this.mapProjection_) {
          if (!this.transform_) {
            const projection = this.getProjection();
            if (projection) {
              this.transform_ = getTransformFromProjections(
                this.mapProjection_,
                projection
              );
            } else {
              this.transform_ = identityTransform;
            }
          }
          const map = this.getMap();
          const coordinate = map.getCoordinateFromPixelInternal(pixel);
          if (coordinate) {
            const userProjection = getUserProjection();
            if (userProjection) {
              this.transform_ = getTransformFromProjections(
                this.mapProjection_,
                userProjection
              );
            }
            this.transform_(coordinate, coordinate);
            const coordinateFormat = this.getCoordinateFormat();
            if (coordinateFormat) {
              val = coordinateFormat(coordinate);
            } else {
              val = coordinate.toString();
            }
          }
        }

        if (!this.renderedHTML_ || val !== this.renderedHTML_) {
          if(this.innerElement_) {
            if (typeof this.innerElement_.value !== 'undefined') {
              this.innerElement_.value = val;
            }else{
              this.innerElement_.innerHTML = val;
            }
          }
          this.renderedHTML_ = val;
          this.updateInputSize(this.innerElement_);
        }
    }

    /**
     * Update the projection. Rendering of the coordinates is done in
     * `handleMouseMove` and `handleMouseUp`.
     * @param {import("../MapEvent.js").default} mapEvent Map event.
     * @override
     */
    render(mapEvent) {
        const frameState = mapEvent.frameState;
        if (!frameState) {
          this.mapProjection_ = null;
        } else {
          if (this.mapProjection_ != frameState.viewState.projection) {
            this.mapProjection_ = frameState.viewState.projection;
            this.transform_ = null;
          }
        }
        if (this.mapProjection_ && !this.getProjection()) {
            this.setProjection(this.mapProjection_);
            this.projectionSelect_.value = this.mapProjection_.getCode();
        }
    }

    setProjection(projection) {
      super.setProjection(projection);
      this.dispatchEvent(
        new ViewProjectionEvent('change:projection', projection),
      );
    }
}

export default MousePosition;